import { useLocalStorage } from "@vueuse/core";
import { createRouter, createWebHistory } from "vue-router";
import Bars from "@/components/icons/Bars.vue";
import Bullseye from "@/components/icons/Bullseye.vue";
import ChartArea from "@/components/icons/ChartArea.vue";
import Earth from "@/components/icons/Earth.vue";
import MagnifyingGlass from "@/components/icons/MagnifyingGlass.vue";
import UserPen from "@/components/icons/UserPen.vue";
import UserPlus from "@/components/icons/UserPlus.vue";
import Users from "@/components/icons/Users.vue";
import useDomainStore from "@/stores/domain";
import useNotificationStore from "@/stores/notifications";
import useUserStore from "@/stores/user";
import AboutEditor from "@/views/AboutEditor.vue";
import ActivityLog from "@/views/ActivityLog.vue";
import AgentOverview from "@/views/AgentOverview.vue";
import AgentSearch from "@/views/AgentSearch.vue";
import Dashboard from "@/views/Dashboard.vue";
import DomainOverview from "@/views/DomainOverview.vue";
import DomainSearch from "@/views/DomainSearch.vue";
import Login from "@/views/Login.vue";
import Logout from "@/views/Logout.vue";
import MissionEditor from "@/views/MissionEditor.vue";
import PageNotFound from "@/views/PageNotFound.vue";
import ParticipantsListUpload from "@/views/ParticipantsListUpload.vue";
import RouterViewLayout from "@/views/RouterViewLayout.vue";
import TeamEditor from "@/views/TeamEditor.vue";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/login/", name: "Login", component: Login },
    { path: "/logout/", name: "Logout", component: Logout },
    {
      path: "/:vendor/",
      component: RouterViewLayout,
      children: [
        {
          path: "",
          name: "Dashboard",
          component: Dashboard,
          meta: { icon: ChartArea, navGroup: "home", navOrder: 1 },
        },
        {
          path: "domains/",
          name: "Domains",
          component: DomainSearch,
          meta: { icon: MagnifyingGlass, navGroup: "home", navOrder: 2 },
        },
        {
          path: "agents/",
          name: "Agents",
          component: AgentSearch,
          meta: { icon: Users, navGroup: "home", navOrder: 3 },
        },
        {
          path: "activity-log/",
          name: "Activity Log",
          component: ActivityLog,
          meta: { icon: Bars, navGroup: "home", navOrder: 4 },
        },
        {
          path: "agent/:alias/",
          name: "Agent Overview",
          component: AgentOverview,
          meta: { icon: Earth },
        },
        {
          path: "edit/:domain/",
          component: RouterViewLayout,
          children: [
            {
              path: "",
              name: "Domain Overview",
              component: DomainOverview,
              meta: { icon: Earth, navGroup: "editor", navOrder: 1 },
            },
            {
              path: "about/",
              name: "About Me",
              component: AboutEditor,
              meta: { icon: UserPen, navGroup: "editor", navOrder: 2 },
            },
            {
              path: "mission/",
              name: "Mission",
              component: MissionEditor,
              meta: { icon: Bullseye, navGroup: "editor", navOrder: 3 },
            },
            {
              path: "team/",
              name: "Team - Members",
              component: TeamEditor,
              meta: { icon: UserPlus, navGroup: "editor", navOrder: 4 },
            },
          ],
        },
        {
          path: "admin/participants-list/",
          name: "Participants List Upload",
          component: ParticipantsListUpload,
          meta: {
            isAdmin: true,
            permission: "can_upload_vendor_participants_list",
            navOrder: 100,
          },
        },
        { path: "/:pathMatch(.*)", component: PageNotFound },
      ],
    },
  ],
});

// redirect to login page if not logged in and trying to access a restricted page
router.beforeEach(async (to) => {
  const publicPages = ["/login", "/login/", "/logout", "/logout/"];
  const authRequired = !publicPages.includes(to.path);
  const notificationStore = useNotificationStore();

  if (authRequired) {
    const domainStore = useDomainStore();
    const userStore = useUserStore();

    // First we check to see if we are authenticated by attempting to load user data
    try {
      await userStore.ensureUser();
    } catch (_error) {
      notificationStore.addNotification(
        "There was an error getting user information",
        "red",
      );
    }

    // If that fails we redirect to the login page
    if (!userStore.userExists) {
      useLocalStorage("authReturnPath").value = to.path;
      return "/login";
    }

    // If navigated to root, redirect with vendor key included
    if (to.path === "/") {
      return `/${userStore.vendorKey}/`;
    }

    // Ensure valid domain
    if (to.params.domain) {
      try {
        await domainStore.ensureDomain(to.params.domain);
      } catch (_error) {
        notificationStore.addNotification(
          "There was an error getting domain information. Please try again later",
          "red",
        );
        return `/${userStore.vendorKey}/`;
      }
    }

    // If no vendor key, (/login, /logout, etc.), we have nothing left to check
    if (!to.params.vendor) {
      return true;
    }

    // If vendor key matches user's vendor, we're good
    if (to.params.vendor === userStore.vendorKey) {
      return true;
    }

    // Redirect back to user's vendor
    if (!userStore.isMirusUser) {
      return {
        name: to.name,
        params: {
          ...to.params,
          vendor: userStore.vendorKey,
        },
      };
    }

    // At this point, it's a Mirus user who is switching the vendor in the URL

    // Ensure vendor in URL is a real vendor
    await userStore.ensureVendorOptions();
    const vendorExists = userStore.vendorOptions.content.some(
      (v) => v.key === to.params.vendor,
    );
    if (!vendorExists) {
      return `/${userStore.vendorKey}/`;
    }

    // Update selected vendor in local storage
    if (to.params.vendor === "all") {
      useLocalStorage("vendor").value = "";
    } else {
      useLocalStorage("vendor").value = to.params.vendor;
    }
    // and get new domain data with newly selected vendor
    if (to.params.domain) {
      try {
        await domainStore.getDomain(to.params.domain, to.params.vendor);
      } catch (_error) {
        notificationStore.addNotification(
          "There was an error getting domain data for this vendor.",
          "red",
        );
        return `/${userStore.vendorKey}/`;
      }
    }
    return true;
  }
});

export default router;
